import { useEffect } from 'react';
import { useUiStore, CHAPTERS } from '@/store/useUiStore';

/**
 * Keep the Zustand reducedMotion flag in sync with the OS setting live,
 * so the Value Field and choreography can react without a reload (plan §6, §9).
 */
export function useReducedMotionSync() {
  const setReducedMotion = useUiStore((s) => s.setReducedMotion);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      setReducedMotion(mq.matches);
      document.documentElement.toggleAttribute('data-reduced-motion', mq.matches);
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [setReducedMotion]);
}

/**
 * Scroll-spy: sets the active chapter to whichever section is crossing the
 * viewport's vertical centre, so the nav dots track scroll position (plan §4).
 * Uses a scroll listener (not IntersectionObserver) so it stays responsive
 * even where IO callbacks are render-throttled.
 */
export function useScrollSpy() {
  const setActiveChapter = useUiStore((s) => s.setActiveChapter);
  useEffect(() => {
    let last = '';
    const compute = () => {
      const center = window.innerHeight / 2;
      let best: string | null = null;
      let bestDist = Infinity;
      for (const id of CHAPTERS) {
        const el = document.getElementById(id);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.top <= center && r.bottom >= center) {
          best = id; // section straddles the centre — definitive
          break;
        }
        const dist = Math.min(Math.abs(r.top - center), Math.abs(r.bottom - center));
        if (dist < bestDist) {
          bestDist = dist;
          best = id;
        }
      }
      if (best && best !== last) {
        last = best;
        setActiveChapter(best as (typeof CHAPTERS)[number]);
      }
    };
    // light throttle via timestamp (no rAF, so it also runs when rAF is throttled)
    let t = 0;
    const onScroll = () => {
      const now = Date.now();
      if (now - t < 80) return;
      t = now;
      compute();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    compute();
    return () => window.removeEventListener('scroll', onScroll);
  }, [setActiveChapter]);
}
